import React, { useMemo, useCallback, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import ExcelTable from 'components/ui/excel-table';
import {
  ExcelTableColDef,
  ECellValueType,
  EExcelTableColumnType,
  AllRowsData,
  RowData,
} from 'components/ui/excel-table/types';
import { ContractUnAllocationTableProps } from './types';
import { TaskIcon } from 'assets/icons/task';
import FormActionButtons from 'components/ui/form-action-buttons';
import { ContractAllocationRow } from 'components/features/contract-allocation-table/types';

const ContractUnAllocationTable: React.FC<ContractUnAllocationTableProps> = ({
  data,
  onContractUnallocated,
}) => {
  const { t } = useTranslation();
  const [tableData, setTableData] = useState<ContractAllocationRow[]>(data);

  useEffect(() => {
    setTableData(data);
  }, [data]);

  // Column definitions
  const columnDefs = useMemo(
    (): ExcelTableColDef<ContractAllocationRow>[] => [
      {
        headerName: '',
        headerComponent: () => (
          <div className="flex items-center justify-center w-full h-full">
            <TaskIcon />
          </div>
        ),
        field: 'isSelected',
        width: 100,
        columnType: EExcelTableColumnType.CHECKBOX,
        cellStyle: {
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        },
      },
      {
        headerName: t('contractAllocation.documentNo'),
        field: 'documentNo',
        flex: 1,
        editable: false,
        resizable: true,
      },
      {
        headerName: t('contractAllocation.insured'),
        field: 'insured',
        flex: 1,
        editable: false,
        resizable: true,
      },
      {
        headerName: t('contractAllocation.reinsured'),
        field: 'reinsured',
        flex: 1,
        editable: false,
        resizable: true,
      },
      {
        headerName: t('contractAllocation.binder'),
        field: 'binder',
        flex: 1,
        editable: false,
        resizable: true,
      },
      {
        headerName: t('contractAllocation.netAmountFromBroker'),
        field: 'paidAmount',
        width: 150,
        type: ECellValueType.NUMERIC_COLUMN,
        precision: 2,
        editable: false,
        resizable: true,
      },
    ],
    [t]
  );

  const onDataChange = useCallback(
    (
      _: RowData<ContractAllocationRow>,
      allRows: AllRowsData<ContractAllocationRow>
    ) => {
      setTableData(allRows.data);
    },
    [setTableData]
  );
  const selectedRows = useMemo(() => {
    return tableData.filter((row) => row.isSelected);
  }, [tableData]);

  return (
    <div className="flex flex-col gap-4">
      <ExcelTable
        tableContainerClassName="h-[15rem]"
        data={tableData.map((row) => ({
          ...row,
          paidAmount: row.settlementExchangeRate
            ? row.settlementExchangeRate * row.originalNetAmountDueBroker
            : undefined,
        }))}
        columnDefs={columnDefs}
        onDataChange={onDataChange}
        rowKeyColName="id"
        getRowClass={(params) => {
          if (params.data && params.data.isSelected) {
            return 'excel-table-row-selected';
          }
          return '';
        }}
      />
      <FormActionButtons
        isConfirmDisabled={selectedRows.length === 0}
        includeCancel={false}
        onConfirm={() => {
          onContractUnallocated(selectedRows);
        }}
        confirmOutlined={false}
        confirmText={t('contractAllocation.unallocate')}
        isFormSubmit={false}
      />
    </div>
  );
};

export default ContractUnAllocationTable;
